import { useMemo } from "react";
import { ConditionEvaluator } from "../utils/conditionEvaluator";
import { ControlI } from "../typings/routesTypes";

export interface FieldConditionState {
  isEnabled: boolean;
  shouldIncludeInSubmission: boolean;
}

export interface FormFieldConditions {
  [fieldName: string]: FieldConditionState;
}

/**
 * Hook to manage form field conditions based on current form data
 * @param controls Array of form controls
 * @param formData Current form data
 * @returns Object containing field condition states
 */
export const useFormFieldConditions = (
  controls: ControlI[][],
  formData: any
): FormFieldConditions => {
  return useMemo(() => {
    const fieldConditions: FormFieldConditions = {};

    // Flatten controls and evaluate conditions
    const flattenedControls = controls.flat();
    
    flattenedControls.forEach((control) => {
      if (control.field) {
        const isEnabled = control.condition
          ? ConditionEvaluator.evaluateCondition(control.condition, formData || {})
          : true;

        fieldConditions[control.field] = {
          isEnabled,
          shouldIncludeInSubmission: isEnabled
        };

        // Handle controls with multiple fields (like money controls with currency field)
        if (control.currencyField) {
          fieldConditions[control.currencyField] = {
            isEnabled,
            shouldIncludeInSubmission: isEnabled
          };
        }

        // Handle multi-level controls
        if (control.child) {
          const processChild = (child: any, enabled: boolean) => {
            if (child.field) {
              fieldConditions[child.field] = {
                isEnabled: enabled,
                shouldIncludeInSubmission: enabled
              };
            }
            if (child.child) {
              processChild(child.child, enabled);
            }
          };
          processChild(control.child, isEnabled);
        }

        // Handle multi-field selector controls
        if (control.options) {
          control.options.forEach((option: any) => {
            if (option.field) {
              fieldConditions[option.field] = {
                isEnabled,
                shouldIncludeInSubmission: isEnabled
              };
            }
          });
        }

        // Handle tyres control children
        if (control.children) {
          control.children.forEach((child: any) => {
            if (child.field) {
              fieldConditions[child.field] = {
                isEnabled,
                shouldIncludeInSubmission: isEnabled
              };
            }
          });
        }

        // Handle map controls
        if (control.latitudeField) {
          fieldConditions[control.latitudeField] = {
            isEnabled,
            shouldIncludeInSubmission: isEnabled
          };
        }
        if (control.longitudeField) {
          fieldConditions[control.longitudeField] = {
            isEnabled,
            shouldIncludeInSubmission: isEnabled
          };
        }
      }
    });

    return fieldConditions;
  }, [controls, formData]);
};

/**
 * Filters form data to exclude disabled fields
 * @param formData The complete form data
 * @param fieldConditions The field conditions from useFormFieldConditions
 * @returns Filtered form data with only enabled fields
 */
export const filterEnabledFormData = (
  formData: any,
  fieldConditions: FormFieldConditions
): any => {
  if (!formData) return formData;

  const filteredData = { ...formData };

  // Remove fields that are disabled
  Object.keys(fieldConditions).forEach(fieldName => {
    if (!fieldConditions[fieldName].shouldIncludeInSubmission) {
      delete filteredData[fieldName];
    }
  });

  return filteredData;
};


// WEBPACK FOOTER //
// ./src/hooks/useFormFieldConditions.ts